import { useCookies } from '@/app/cookie-provider';
import BadgeStoreFront from '@/components/badge-storefront';
import DynamicImage from '@/components/commons/dynamic-image';
import PriceBookIcon from '@/components/icons/price-book-icon';
import ModalAlert from '@/components/sections/modal-alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import {
  useMutateApplyPromotion,
  useMutateValidatePromotion,
} from '@/hooks/api/storefront/promotion';
import { useDebounceValue } from '@/hooks/use-debounce';
import { useFreeGiftWizard } from '@/hooks/use-free-gift-wizard';
import useModal from '@/hooks/useModal';
import { deleteCartItem, updateCartItem } from '@/lib/actions/cart/action';
import { updateCartCheckout } from '@/lib/actions/checkout/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { buildAutoFreeGifts, extractErrorInfo } from '@/lib/promotions';
import { queryClient } from '@/lib/query-client';
import {
  calculateTotalDiscountPerPriority,
  cn,
  formatIDR,
  formatNumber,
  generatePromotionsPayload,
  hexToRgba,
  isEmpty,
  parseErrorMessage,
  truncateText,
} from '@/lib/utils';
import { ThemeColorSchemeType, ThemeType } from '@/stores/builder';
import { usePromotionStore } from '@/stores/promotion';
import { HttpTypes } from '@/types';
import { StoreCart } from '@/types/http';
import { BaseCart } from '@/types/http/cart/common';
import { BasePromotion } from '@/types/http/promotion/common';
import { isAxiosError } from 'axios';
import {
  ChevronRight,
  CircleAlert,
  Gift,
  Loader2,
  MinusIcon,
  PlusIcon,
  TicketPercent,
  Trash2,
  XIcon,
} from 'lucide-react';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment, useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';

type WizardAPI = ReturnType<typeof useFreeGiftWizard>;

type CartListProps = {
  cart: HttpTypes.StoreCart;
  cartItems: BaseCart[];
  host: string;
  token?: string;
  isPreview?: boolean;
  theme: ThemeType;
  themeId?: string;
  currentItemQty: { [key: string]: number };
  subtotal?: number;
  setCurrentItemQty: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>;
  setOpenModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerView?: React.Dispatch<
    React.SetStateAction<'cart' | 'voucher' | 'gift' | 'active_offers'>
  >;
  recommendPromotion?: BasePromotion[];
  isLoadingRecommend?: boolean;
  payloadListPromotion: {
    items: {
      item_id: string;
      qty: number;
      amount: number;
    }[];
    grand_total: number;
    current_date: string;
  };
  colorScheme?: ThemeColorSchemeType;
  wizard?: WizardAPI;
  isLoadingData?: boolean;
  pendingQtyRef: React.RefObject<{ [key: string]: number }>;
  originalQtyRef: React.RefObject<{ [key: string]: number }>;
};

export default function CartList({
  cart,
  cartItems,
  host,
  token,
  isPreview,
  theme,
  themeId,
  currentItemQty,
  subtotal,
  setCurrentItemQty,
  setOpenModal,
  setDrawerView,
  recommendPromotion,
  isLoadingRecommend,
  payloadListPromotion,
  colorScheme,
  wizard,
  isLoadingData,
  pendingQtyRef,
  originalQtyRef,
}: CartListProps) {
  const { multiOrigin } = useCookies();
  const router = useRouter();
  const pathname = usePathname();
  const modalConfirmSpecific = useModal<{
    onConfirm: () => void;
    onCancel?: () => void;
  }>();

  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
  const [draftQtyMap, setDraftQtyMap] = useState<Record<string, number>>({});

  const debouncedDraftQtyMap = useDebounceValue<Record<string, number>>(draftQtyMap, 500);

  const {
    selectedPromotion,
    selectedFreeGift,
    replacePromotion,
    removePromotion,
    replaceFreeGift,
    removeFreeGift,
    removeFreeGiftByItemId,
  } = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      selectedFreeGift: state.selectedFreeGift,
      removePromotion: state.removePromotion,
      replacePromotion: state.replacePromotion,
      removeFreeGift: state.removeFreeGift,
      replaceFreeGift: state.replaceFreeGift,
      removeFreeGiftByItemId: state.removeFreeGiftByItemId,
    }),
    shallow
  );

  const hasSelectedPromos = selectedPromotion.length > 0;
  const selectedPromosName = selectedPromotion.map((p) => p.promotion_name).join(', ');

  const { mutateAsync: mutateApplyPromotion } = useMutateApplyPromotion({
    onError: async (error) => {
      if (isAxiosError(error)) {
        if (error.status === 401) {
          await fetch('/api/remove-cart-cookie', { method: 'GET' });

          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Something went wrong',
            variant: 'destructive',
          });

          return (window.location.href = '/');
        }
      }
    },
  });

  const validatePromotion = useMutateValidatePromotion({
    onSuccess: (data) => {
      const isValid = data.data.is_valid;
      const promoRewards = data.data.promotion_rewards;
      if (isValid && promoRewards) {
        promoRewards.forEach((promo: BasePromotion) => {
          replacePromotion(promo);

          if ((promo?.product_reward?.length ?? 0) > 0) {
            const initialFreeGift = buildAutoFreeGifts(promo);
            if (initialFreeGift) {
              replaceFreeGift(promo.promotion_id, initialFreeGift);
            }
          }
        });
      }
    },
  });

  const getUpdatedItems = (quantities: typeof currentItemQty, items: typeof cartItems) => {
    return items.map((item) => {
      const pending = pendingQtyRef.current[item.id];
      return {
        ...item,
        quantity: pending ?? quantities[item.id] ?? item.quantity,
      };
    });
  };

  const applyQtyImmediate = (item: StoreCart, qty: number) => {
    if (originalQtyRef.current[item.id] === undefined) {
      originalQtyRef.current[item.id] = currentItemQty[item.id] ?? item.quantity;
    }

    setCurrentItemQty((prev) => ({ ...prev, [item.id]: qty }));
    setDraftQtyMap((prev) => ({ ...prev, [item.id]: qty }));
    pendingQtyRef.current[item.id] = qty;
  };

  const tryValidatePromotion = async (customItems: typeof cartItems) => {
    const promotions = selectedPromotion
      .filter(
        (promo) =>
          promo.product_rules_type === 'specific' ||
          promo.product_rules_type === 'flexible' ||
          promo.use_voucher
      )
      .map((promo) => ({
        promotion_id: promo.promotion_id,
        voucher_code: promo.voucher_code ?? '',
      }));

    if (promotions.length === 0) return;

    const items = customItems.map((item) => {
      return {
        item_id: item.id,
        qty: item.quantity,
        amount: Number(item.sell_price),
      };
    });

    const payload = {
      items,
      promotions,
      grand_total: items.reduce((sum, item) => sum + item.amount * item.qty, 0),
    };

    await validatePromotion.mutateAsync({
      body: payload,
      token: token as string,
    });
  };

  const processQtyChange = async (item: StoreCart, qty: number) => {
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));

    const baseItems = getUpdatedItems(currentItemQty, cartItems);
    const updatedItemsDesired = baseItems.map((i) =>
      i.id === item.id ? { ...i, quantity: qty } : i
    );

    let handledByModal = false;

    try {
      await tryValidatePromotion(updatedItemsDesired);

      setCurrentItemQty((prev) => ({ ...prev, [item.id]: qty }));

      await updateCartItem(host as string, { ...item, quantity: qty });
      queryClient.invalidateQueries({ queryKey: ['carts'] });

      delete pendingQtyRef.current[item.id];
      delete originalQtyRef.current[item.id];
    } catch (error) {
      if (isAxiosError(error)) {
        const raw = error.response?.data ?? error.message ?? 'Unknown error';
        const info = extractErrorInfo(raw);

        if (info?.promotionId) {
          handledByModal = true;

          modalConfirmSpecific.open({
            onConfirm: async () => {
              const qtyToCommit = pendingQtyRef.current[item.id] ?? qty;

              removePromotion(info.promotionId as string);

              setCurrentItemQty((prev) => ({ ...prev, [item.id]: qtyToCommit }));

              await updateCartItem(host as string, { ...item, quantity: qty });
              await queryClient.invalidateQueries({ queryKey: ['carts'] });

              setCurrentItemQty((prev) => ({ ...prev, [item.id]: qtyToCommit }));

              delete pendingQtyRef.current[item.id];
              delete originalQtyRef.current[item.id];
              setDraftQtyMap((prev) => {
                const rest = { ...prev };
                delete rest[item.id];
                return rest;
              });
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
              modalConfirmSpecific.close();
            },
            onCancel: () => {
              const originalQty = originalQtyRef.current[item.id] ?? item.quantity;
              setCurrentItemQty((prev) => ({ ...prev, [item.id]: originalQty }));
              delete pendingQtyRef.current[item.id];
              delete originalQtyRef.current[item.id];
              setDraftQtyMap((prev) => {
                const rest = { ...prev };
                delete rest[item.id];
                return rest;
              });
              setIsLoading((prev) => ({ ...prev, [item.id]: false }));
              modalConfirmSpecific.close();
            },
          });
        } else {
          const originalQty = originalQtyRef.current[item.id] ?? item.quantity;
          setCurrentItemQty((prev) => ({ ...prev, [item.id]: originalQty }));
          delete pendingQtyRef.current[item.id];
          delete originalQtyRef.current[item.id];
        }
      } else {
        const originalQty = originalQtyRef.current[item.id] ?? item.quantity;
        setCurrentItemQty((prev) => ({ ...prev, [item.id]: originalQty }));
        delete pendingQtyRef.current[item.id];
        delete originalQtyRef.current[item.id];
      }
    } finally {
      if (!handledByModal) {
        setIsLoading((prev) => ({ ...prev, [item.id]: false }));
      }
    }
  };

  const debouncedQtyChange = (item: StoreCart, qty: number) => {
    applyQtyImmediate(item, qty);
  };

  const handleIncrementQty = (item: StoreCart) => {
    const newQty = (currentItemQty[item.id] || item.quantity) + 1;
    if (newQty > (cart?.item_stock?.find((i) => i.item_id === item.id)?.stock || 0)) return;
    debouncedQtyChange(item, newQty);
  };

  const handleDecrementQty = (item: StoreCart) => {
    const currentQty = currentItemQty[item.id] ?? item.quantity;
    const newQty = Math.max(1, currentQty - 1);
    debouncedQtyChange(item, newQty);
  };

  const removeItem = async (item: StoreCart) => {
    if (isPreview) return;
    setIsLoading((prev) => ({ ...prev, [item.id]: true }));

    const remaining = cartItems.filter((i) => i.id !== item.id);
    const updatedItems = getUpdatedItems(currentItemQty, remaining);

    try {
      await tryValidatePromotion(updatedItems);
      await deleteCartItem(host as string, item.id);
      queryClient.invalidateQueries({ queryKey: ['carts'] });

      // Send analytics events
      SendEventAnalytic('remove_from_cart', {
        currency: 'IDR',
        value: Number(item.sell_price) * item.quantity,
        items: [
          {
            item_name: item?.product_name,
            item_id: item.id as string,
            price: Number(item?.sell_price),
            quantity: item.quantity,
            unique_action_id: Date.now(),
          },
        ],
      });
      SendEventMetapixel('RemoveFromCart', {
        content_ids: [item.id],
        content_name: item.product_name,
        content_type: 'product',
        quantity: item.quantity,
        value: Number(item.sell_price) * item.quantity,
        currency: 'IDR',
      });
    } catch (error) {
      if (isAxiosError(error)) {
        const raw = error.response?.data ?? error.message ?? 'Unknown error';
        const info = extractErrorInfo(raw);

        modalConfirmSpecific.open({
          onConfirm: async () => {
            removePromotion(info.promotionId as string);
            modalConfirmSpecific.close();

            await deleteCartItem(host as string, item.id);
            queryClient.invalidateQueries({ queryKey: ['carts'] });

            // Send analytics events
            SendEventAnalytic('remove_from_cart', {
              currency: 'IDR',
              value: Number(item.sell_price) * item.quantity,
              items: [
                {
                  item_name: item?.product_name,
                  item_id: item.id as string,
                  price: Number(item?.sell_price),
                  quantity: item.quantity,
                  unique_action_id: Date.now(),
                },
              ],
            });
            SendEventMetapixel('RemoveFromCart', {
              content_ids: [item.id],
              content_name: item.product_name,
              content_type: 'product',
              quantity: item.quantity,
              value: Number(item.sell_price) * item.quantity,
              currency: 'IDR',
            });
          },
          onCancel: () => {
            modalConfirmSpecific.close();
          },
        });
      }
    } finally {
      setIsLoading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleToCart = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, ['checkout']: true }));

      if (isPreview) return;

      if (selectedPromotion.length > 0) {
        const promotions_payload = {
          cart_id: cart.cartId as string,
          promotions: generatePromotionsPayload(selectedPromotion, selectedFreeGift),
        };

        await mutateApplyPromotion({
          body: promotions_payload ?? { cart_id: cart.id, promotions: [] },
          token,
        });
      }

      const result = await updateCartCheckout(host as string);

      if (result.success) {
        return router.push(`/checkout/${result.data.checkout_id}`);
      }

      if (!cartItems?.length) {
        return toast({
          title: 'Error',
          description: 'Cart is empty',
          variant: 'destructive',
        });
      }

      if (result.status === 404) {
        await fetch('/api/remove-cart-cookie', { method: 'GET' });

        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });

        return (window.location.href = '/');
      }

      if (result.status === 400) {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage = error.response?.data.data || error.response?.data?.message;
        toast({
          title: 'Error',
          description: parseErrorMessage(errorMessage) || 'Something went wrong',
          variant: 'destructive',
        });

        if (error.status === 401) {
          await fetch('/api/remove-cart-cookie', { method: 'GET' });

          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Something went wrong',
            variant: 'destructive',
          });

          return (window.location.href = '/');
        }
      }
    } finally {
      setOpenModal?.(false);
      setIsLoading((prev) => ({ ...prev, ['checkout']: false }));
    }
  };

  const handleStock = (itemId: string) => {
    return Number(cart?.item_stock?.find((item) => item.item_id === itemId)?.stock);
  };

  const checkStockAfterChangeOrigin = (itemId: string, quantity: number): boolean => {
    const stockValue = cart?.item_stock?.find((item) => item.item_id === itemId)?.stock;
    const stock = Number(stockValue) || 0;
    return quantity > stock;
  };
  const handleDisableAction = (itemId: string, quantity: number): boolean => {
    const stockValue = cart?.item_stock?.find((item) => item.item_id === itemId)?.stock;
    const stock = Number(stockValue) || 0;
    return quantity >= stock;
  };

  useEffect(() => {
    const entries = Object.entries(debouncedDraftQtyMap);

    entries.forEach(([itemId, qty]) => {
      const hasPending = pendingQtyRef.current[itemId] !== undefined;
      if (!hasPending) return;

      const item = cartItems.find((i) => i.id === itemId);
      if (!item) {
        delete pendingQtyRef.current[itemId];
        return;
      }

      processQtyChange(item, qty);
    });
  }, [debouncedDraftQtyMap, cartItems]);

  const disableButton = cart?.item_stock?.some((item) => Number(item.stock) <= 0);
  const discount = calculateTotalDiscountPerPriority(selectedPromotion, subtotal as number);

  const unfulfilledCount = wizard?.outstandingPromos?.length ?? 0;

  return (
    <>
      <DrawerHeader className='flex items-center justify-between text-left'>
        <DrawerTitle className='theme_cart_drawer_color'>Your Cart</DrawerTitle>
        <DrawerClose>
          <XIcon className='h-4 w-4' />
        </DrawerClose>
      </DrawerHeader>
      <div className='px-4'>
        <div className='theme_cart_drawer_color grid grid-cols-6 border-b py-2 text-xs uppercase'>
          <div className='col-span-4'>Product</div>
          <div className='col-span-2 text-right'>Total</div>
        </div>
      </div>
      <ScrollArea
        className='h-full overflow-y-auto px-4'
        type='scroll'
        style={
          {
            '--background': hexToRgba(colorScheme?.background, 0.1) || '#F3F4F6',
            '--text-primary': colorScheme?.text || '#111827',
            '--text-secondary': colorScheme?.textSecondary || '#6B7280',
            '--btn-bg': colorScheme?.btnSolidBackgroud,
            '--btn-label': colorScheme?.btnSolidlabel,
          } as React.CSSProperties
        }
      >
        {!isLoadingData && unfulfilledCount > 0 && (
          <div className='my-2 flex flex-col gap-2 rounded-sm bg-yellow-100 p-1'>
            <div className='flex w-full items-center justify-between gap-2 rounded-sm p-1'>
              <div className='flex items-center gap-2'>
                <Gift className='h-6 w-6 shrink-0' />
                <div className='flex flex-col gap-1'>
                  <p className='text-sm'>
                    <span className='font-medium'>You&apos;ve unlocked a FREE GIFT!</span> Choose
                    your free gift now.
                  </p>
                </div>
              </div>
              <Button
                variant={null}
                onClick={() => {
                  setDrawerView?.('gift');
                  wizard?.start();
                }}
                className='w-auto bg-black/10 text-xs text-black hover:bg-black/20'
              >
                Choose Gifts ({unfulfilledCount})
              </Button>
            </div>
          </div>
        )}
        {/* Cart Items */}
        {cartItems?.map((item) => {
          const images = (item.product_assets || []).filter(
            (asset) => asset.assets_type === 'image'
          );
          const displayedQuantity = currentItemQty[item.id] || item.quantity;
          const lowestPrice = Number(
            Number(item.discount_price) > 0 && Number(item.discount_price) < Number(item.sell_price)
              ? item.discount_price
              : item.sell_price
          );
          const totalPrice = displayedQuantity * lowestPrice;
          const stock = handleStock(item.id);
          const isOutOfStock = stock <= 0;
          const disable = handleDisableAction(item.id, item.quantity);
          const showNotifyStock = checkStockAfterChangeOrigin(item.id, item.quantity);
          const suggestionStock = (itemId: string) => {
            return cart?.item_stock?.find((item) => item.item_id === itemId)?.available_stock || [];
          };
          const suggestionPricebook = (itemId: string) => {
            return cart?.items?.find((item) => item.id === itemId)?.pricebook || [];
          };

          const isAnyPricebook =
            (cart?.items?.find((it) => it.id === item.id)?.pricebook ?? []).length > 0;
          const isUsedPricebook =
            cart?.items?.find((it) => it.id === item.id)?.pricebook_id !== null;

          return (
            <div className='theme_cart_drawer_color flex gap-x-2 border-b py-4' key={item.id}>
              <div className='mt-1 h-24 w-24'>
                <Image
                  src={images[0]?.assets_url}
                  alt={item.product_name}
                  width={80}
                  height={80}
                  className={cn(
                    'aspect-square w-36 border object-contain',
                    isOutOfStock && 'grayscale'
                  )}
                />
              </div>
              <div className='flex flex-1 flex-col gap-y-2'>
                <div className={cn('w-fit', isOutOfStock && 'grayscale')}>
                  <BadgeStoreFront theme={theme} item={item} />
                </div>

                <div className='grid grid-cols-6'>
                  <div className='col-span-4'>
                    <div className='flex gap-x-3'>
                      <div className='w-full space-y-1'>
                        <h2 className={cn('line-clamp-2 text-sm', isOutOfStock && 'text-gray-500')}>
                          {item.product_name}
                        </h2>
                        {item.variants?.map((variant) => (
                          <p
                            key={variant.option_id}
                            className={cn(
                              'line-clamp-2 block w-full text-gray-500',
                              isOutOfStock && 'text-gray-500'
                            )}
                          >
                            {variant.label}
                          </p>
                        ))}
                        <div
                          className={cn(item?.discount_price && 'flex flex-col items-start gap-2')}
                        >
                          <span
                            className={cn(
                              ' text-base font-bold',
                              isOutOfStock ? '!text-gray-500' : 'text_product_secondary'
                            )}
                          >
                            {formatIDR(lowestPrice)}
                          </span>

                          {(item?.discount_price as number) > 0 &&
                          Number(item?.discount_price) < Number(item?.sell_price) &&
                          !isUsedPricebook ? (
                            <span
                              className={cn(
                                'text-sm text-gray-500 line-through',
                                isOutOfStock && 'text-gray-500'
                              )}
                            >
                              {formatIDR(item?.sell_price || 0)}
                            </span>
                          ) : null}
                        </div>
                        <div className='flex gap-x-2 pt-2'>
                          <div className='flex h-8 w-fit items-center rounded border'>
                            <button
                              onClick={() => handleDecrementQty(item)}
                              className='h-full px-3'
                              disabled={stock <= 0}
                            >
                              <MinusIcon
                                className={cn(
                                  'h-3 w-3',
                                  displayedQuantity > 1 ? 'cursor-pointer' : 'cursor-not-allowed'
                                )}
                              />
                            </button>
                            <span className='mx-2'>{formatNumber(displayedQuantity)}</span>
                            <button
                              onClick={() => handleIncrementQty(item)}
                              className={cn('h-full px-3', disable && 'cursor-not-allowed')}
                              disabled={disable}
                            >
                              <PlusIcon
                                className={cn(
                                  'h-3 w-3',
                                  stock <= 0 ? 'cursor-not-allowed' : 'cursor-pointer'
                                )}
                              />
                            </button>
                          </div>
                          <button onClick={() => removeItem(item)} disabled={isLoading[item.id]}>
                            <Trash2 className='h-3 w-3' />
                          </button>
                          {multiOrigin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                                <CircleAlert className='h-3 w-3' />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='center' className='z-[1000] w-[200px]'>
                                <DropdownMenuLabel>See Where It&apos;s Available</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                  {suggestionStock(item.id).length > 0 ? (
                                    suggestionStock(item.id).map((stockItem, index) => (
                                      <DropdownMenuItem
                                        key={index}
                                        className='flex justify-between gap-x-3'
                                      >
                                        <span>{stockItem.location_name}</span>
                                        <span>{stockItem.quantity}</span>
                                      </DropdownMenuItem>
                                    ))
                                  ) : (
                                    <div className='px-2 py-1'>
                                      <span>Out of stock at all locations</span>
                                    </div>
                                  )}
                                </DropdownMenuGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {isAnyPricebook && isUsedPricebook && (
                            <DropdownMenu>
                              <DropdownMenuTrigger className='flex items-center focus:border-none focus:outline-none'>
                                <PriceBookIcon
                                  className='h-4 w-4'
                                  color={theme.cart?.drawerColorScheme?.text}
                                />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='center' className='z-[1000] w-[250px] '>
                                <DropdownMenuLabel className='text-sm'>
                                  Tiered Pricing
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuGroup className='max-h-[200px] overflow-y-auto'>
                                  {suggestionPricebook(item.id).map((item, index) => {
                                    const label = `Buy ${
                                      item.min_unit === 1 ? '1' : `> ${(item?.min_unit ?? 0) - 1}`
                                    } for ${formatIDR(item.retail_price ?? 0)}/unit`;
                                    return (
                                      <DropdownMenuItem
                                        key={index}
                                        className='flex justify-between gap-x-3 text-xs'
                                      >
                                        <span>{label}</span>
                                      </DropdownMenuItem>
                                    );
                                  })}
                                </DropdownMenuGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={cn(
                      ' col-span-2 text-right text-sm',
                      isOutOfStock ? 'text-gray-500' : 'theme_cart_drawer_color'
                    )}
                  >
                    {formatIDR(totalPrice)}
                  </div>
                </div>
                {multiOrigin && (
                  <div className='w-full'>
                    {multiOrigin &&
                      ((stock <= 0 && isOutOfStock) || (stock > 0 && showNotifyStock)) && (
                        <Badge variant='destructive' className='text-[10px]'>
                          {stock <= 0 && isOutOfStock
                            ? 'Stock Not Available'
                            : 'Stock Is Not Enough'}
                        </Badge>
                      )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Selected Free Gifts — same look as cart items */}
        {selectedPromotion?.map((promo) => {
          const gifts = (selectedFreeGift ?? []).filter(
            (g) => g.promotion_id === promo.promotion_id
          );

          if (!gifts.length) return null;

          return (
            <Fragment key={promo.promotion_id}>
              <div className='flex items-center justify-between gap-2 py-1'>
                <div className='flex items-center gap-2 text-xs text-[var(--text-secondary)]'>
                  <Gift className='h-3 w-3 shrink-0' />
                  <span className='text-left'>
                    Free gifts — {promo.promotion_name ?? promo.promotion_id}
                  </span>
                </div>
                <div className='flex shrink-0 items-start gap-1'>
                  <button
                    type='button'
                    className='flex items-center gap-1 whitespace-nowrap rounded bg-[var(--btn-bg)] px-2 py-1 text-xs text-[var(--btn-label)] hover:opacity-80'
                    onClick={() => {
                      setDrawerView?.('gift');
                      wizard?.startEdit(promo.promotion_id);
                    }}
                  >
                    Change
                  </button>
                  <button
                    type='button'
                    className='flex items-center gap-1 whitespace-nowrap rounded border border-red-500 px-2 py-1 text-xs text-red-500 hover:opacity-80'
                    onClick={() => {
                      removeFreeGift(promo.promotion_id);
                    }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              {gifts.flatMap((group) =>
                group.items.map((item) => {
                  const imgSrc = item.product_assets?.[0] ?? '';
                  const productName =
                    item.product_name ?? group.product_group.product_name ?? 'Free item';
                  const variantLabel = item.item_name;
                  const qty = item.qty ?? 0;

                  return (
                    <div
                      className='theme_cart_drawer_color flex gap-x-2 border-b py-4'
                      key={`${promo.promotion_id}-${item.item_id}`}
                    >
                      <div className='mt-1 h-24 w-24'>
                        {imgSrc !== '' && (
                          <DynamicImage
                            src={imgSrc}
                            alt={productName}
                            width={80}
                            height={80}
                            className='aspect-square w-36 border object-contain'
                          />
                        )}
                      </div>

                      <div className='flex flex-1 flex-col gap-y-2'>
                        <div className='grid grid-cols-6'>
                          <div className='col-span-4'>
                            <div className='flex gap-x-3'>
                              <div className='w-full space-y-1'>
                                <h2 className='line-clamp-2 text-sm'>{productName}</h2>

                                {item.is_variants ? (
                                  <p className='line-clamp-2 text-gray-500'>{variantLabel}</p>
                                ) : null}

                                <div className='flex gap-x-2 pt-2'>
                                  <div className='flex h-8 w-fit items-center rounded border'>
                                    <button className='h-full px-3' disabled>
                                      <MinusIcon className='h-3 w-3' />
                                    </button>
                                    <span className='mx-2'>{formatNumber(qty)}</span>
                                    <button className='h-full px-3' disabled>
                                      <PlusIcon className='h-3 w-3' />
                                    </button>
                                  </div>

                                  <button
                                    type='button'
                                    onClick={() =>
                                      removeFreeGiftByItemId(promo.promotion_id, item.item_id)
                                    }
                                  >
                                    <Trash2 className='h-4 w-4' />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className='theme_cart_drawer_color col-span-2 flex items-start justify-end text-right text-sm'>
                            <div className='flex flex-col gap-1'>
                              <Badge
                                variant='custom'
                                className='theme_badge_inside theme_badge_promotion ml-auto w-fit text-[12px] font-bold'
                              >
                                Free
                              </Badge>
                              <div className='flex flex-col items-start gap-2'>
                                {Number(item.sell_price) > 0 && (
                                  <span className='text-sm text-gray-500 line-through'>
                                    {formatIDR(Number(item.sell_price))}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </Fragment>
          );
        })}
      </ScrollArea>
      <DrawerFooter
        className='border-t px-0 pt-0'
        style={
          {
            '--text-primary': colorScheme?.text || '#111827',
            '--text-secondary': colorScheme?.textSecondary || '#6B7280',
            '--background': hexToRgba(colorScheme?.background as string, 0.1) || '#F3F4F6',
            '--btn-background': colorScheme?.btnSolidBackgroud || '#F3F4F6',
            '--btn-label': colorScheme?.btnSolidlabel || '#111827',
          } as React.CSSProperties
        }
      >
        {/* Promotion */}
        <div className='flex flex-col gap-1'>
          {isLoadingRecommend ? (
            <div className='flex w-full flex-col gap-1.5 px-4 py-2'>
              <Skeleton className='h-20 w-full' />
            </div>
          ) : (
            <>
              <div className='relative flex h-full w-full'>
                {(recommendPromotion ?? [])?.map((promo, index) => {
                  const grandTotal = payloadListPromotion.grand_total;
                  const minSpending = promo.minimum_spending;
                  const spendMore = minSpending - (grandTotal || 0);

                  return (
                    <div className='border-0 px-4 py-2 shadow-sm' key={index}>
                      <Card>
                        <CardContent
                          className={cn(
                            'rounded-lg p-3',
                            !isEmpty(colorScheme)
                              ? 'bg-[--background] text-[--text-primary]'
                              : 'bg-yellow-100 text-black'
                          )}
                        >
                          <div className='flex items-center gap-4'>
                            <Gift className='h-6 w-6 flex-shrink-0' />
                            <p className='text-sm'>
                              Do you want receive more promotion? add{' '}
                              <span className='font-bold'>{formatIDR(spendMore)}</span> to your
                              cart!!!
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
              <div className='relative w-full'>
                <div className='flex w-full items-center bg-[--background] p-3 text-sm font-medium text-[--text-primary]'>
                  <button
                    type='button'
                    className='flex flex-1 items-center justify-start gap-x-2 rounded-none border-b'
                    onClick={() => setDrawerView?.('voucher')}
                    style={{
                      outline: 'none',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <TicketPercent className='size-6' />
                    {hasSelectedPromos ? truncateText(selectedPromosName, 35) : 'See all offers'}
                  </button>
                  <button
                    type='button'
                    onClick={() => setDrawerView?.('active_offers')}
                    className='mr-2 flex items-center rounded-none border-b'
                    style={{
                      outline: 'none',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                    aria-label='Show active offers'
                  >
                    <ChevronRight className='h-5 w-5' />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Subtotal */}
        <div className='theme_cart_drawer_color mb-4 px-4 pt-4'>
          <div className='mb-2 flex items-center justify-between text-base'>
            <span>Estimated total</span>
            <span>{formatIDR(Math.max(Number(subtotal) - discount.totalDiscount, 0))}</span>
          </div>
          {(discount.totalDiscount > 0 || isPreview) && (
            <div
              className='mb-2 flex items-center justify-between text-base text-[var(--color-text-secondary)]'
              style={
                {
                  '--color-text-secondary':
                    theme?.cart?.drawerColorScheme?.textSecondary || '#6B7280',
                } as React.CSSProperties
              }
            >
              <div>Saved</div>
              <div>{formatIDR(discount.totalDiscount || 0)}</div>
            </div>
          )}
          <p>Taxes, discounts and shipping calculated at checkout.</p>
        </div>
        <div className='flex w-full flex-col gap-3 px-4'>
          <Button
            type='button'
            variant='link'
            className='theme_button theme_cart_drawer_btn w-full items-center px-24 underline-offset-2'
            onClick={() => {
              setOpenModal?.(false);
              router.push(`/${token ? `?theme_id=${themeId}&token=${token}` : ''}`);
            }}
            disabled={isLoading['checkout']}
          >
            {isLoading['checkout'] ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              theme?.cart?.labelContinueShopping || 'Continue shopping'
            )}
          </Button>
          <Button
            type='button'
            variant='link'
            className='theme_button theme_cart_drawer_btn flex w-full items-center px-24'
            onClick={handleToCart}
            disabled={isLoading['checkout'] || pathname.startsWith('/checkout') || disableButton}
          >
            {isLoading['checkout'] ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <span className='mx-2'>{theme?.cart?.labelCheckout || 'Checkout'}</span>
            )}
          </Button>
        </div>
      </DrawerFooter>

      <ModalAlert
        title='Remove Active Promotion'
        description='This item currently has an active promotion with specific requirements. If you continue, the promotion will be removed. Are you sure you want to proceed?'
        openModal={modalConfirmSpecific.isShow}
        setOpenModal={(open) => {
          if (!open) {
            modalConfirmSpecific.data?.onCancel?.();
          }
          modalConfirmSpecific.setIsShow(open);
        }}
        onConfirm={modalConfirmSpecific.data?.onConfirm}
      />
    </>
  );
}
